import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  UsersThree,
  MagnifyingGlass,
  Spinner,
  Crown,
  User as UserIcon,
  TrashSimple,
  X,
  Envelope,
  CalendarBlank,
  Buildings,
  Handshake,
  CreditCard,
  ArrowClockwise,
  PencilSimple,
} from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

// ---- helpers -----------------------------------------------------------

function relTime(iso) {
  if (!iso) return "—";
  const now = new Date();
  const then = new Date(iso);
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return then.toLocaleDateString();
}

function shortDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

const TIER_STYLES = {
  free:   { bg: "bg-zinc-800/60",    text: "text-zinc-400",     border: "border-zinc-700" },
  pro:    { bg: "bg-orange-500/15",  text: "text-orange-300",   border: "border-orange-500/40" },
  agency: { bg: "bg-purple-500/15",  text: "text-purple-300",   border: "border-purple-500/40" },
};

const STATUS_STYLES = {
  active:    "text-emerald-400",
  trialing:  "text-amber-400",
  canceled:  "text-red-400",
  past_due:  "text-red-400",
  inactive:  "text-zinc-500",
};

function TierPill({ tier, status }) {
  const t = TIER_STYLES[tier] || TIER_STYLES.free;
  return (
    <div className="inline-flex items-center gap-1.5">
      <span
        data-testid={`user-tier-${tier}`}
        className={`inline-block text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${t.bg} ${t.text} ${t.border}`}
      >
        {tier}
      </span>
      {status && status !== "inactive" && (
        <span className={`text-[10px] font-mono uppercase tracking-wider ${STATUS_STYLES[status] || "text-zinc-500"}`}>
          {status}
        </span>
      )}
    </div>
  );
}

// ---- Detail drawer -----------------------------------------------------

function UserDetailDrawer({ userId, onClose, onUpdated, onDeleted, isMe }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    api.get(`/admin/users/${userId}`)
      .then((r) => {
        if (live) {
          setDetail(r.data);
          setNameValue(r.data.user?.name || "");
        }
      })
      .catch((e) => {
        toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load user");
        onClose();
      })
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [userId, onClose]);

  const saveName = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/users/${userId}`, { name: nameValue });
      toast.success("Name updated");
      setDetail((d) => ({ ...d, user: { ...d.user, name: nameValue } }));
      setEditingName(false);
      onUpdated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleRole = async () => {
    if (isMe) {
      toast.error("You can't change your own role");
      return;
    }
    const newRole = detail.user.role === "admin" ? "user" : "admin";
    setBusy(true);
    try {
      await api.patch(`/admin/users/${userId}`, { role: newRole });
      toast.success(newRole === "admin" ? "Promoted to admin" : "Demoted to user");
      setDetail((d) => ({ ...d, user: { ...d.user, role: newRole } }));
      onUpdated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Role update failed");
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/users/${userId}`);
      toast.success(`Deleted ${data.deleted_email} + ${data.deleted_leads} leads`);
      onDeleted?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const u = detail?.user;
  const sub = detail?.subscription;

  return (
    <div
      data-testid="admin-user-drawer"
      className="fixed inset-y-0 right-0 w-full sm:w-[520px] bg-zinc-950 border-l border-zinc-800 shadow-2xl z-40 overflow-y-auto"
    >
      <div className="sticky top-0 bg-zinc-950/95 backdrop-blur border-b border-zinc-800 px-5 py-4 flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-orange-500">User Detail</div>
          <div className="text-sm text-zinc-300 truncate mt-0.5">{u?.email || "..."}</div>
        </div>
        <button
          data-testid="close-user-drawer"
          onClick={onClose}
          className="text-zinc-500 hover:text-zinc-200"
        >
          <X size={16} weight="bold" />
        </button>
      </div>

      {loading ? (
        <div className="p-12 text-center text-zinc-500">
          <Spinner size={20} className="animate-spin mx-auto" />
        </div>
      ) : !detail ? (
        <div className="p-8 text-sm text-zinc-500">User not found.</div>
      ) : (
        <div className="p-5 space-y-5">
          {/* Identity */}
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-sm p-4">
            <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-2">Identity</div>
            <div className="grid grid-cols-1 gap-2 text-sm">
              <div className="flex items-start gap-2">
                <Envelope size={14} className="text-zinc-500 mt-0.5" />
                <span className="text-zinc-200 font-mono text-[13px] break-all">{u.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <UserIcon size={14} className="text-zinc-500" />
                {editingName ? (
                  <div className="flex items-center gap-2 flex-1">
                    <Input
                      value={nameValue}
                      onChange={(e) => setNameValue(e.target.value)}
                      data-testid="edit-name-input"
                      className="h-8 text-sm bg-zinc-900 border-zinc-700"
                    />
                    <Button
                      onClick={saveName}
                      disabled={busy}
                      size="sm"
                      data-testid="save-name-btn"
                      className="h-8 bg-orange-600 hover:bg-orange-500 text-zinc-950"
                    >
                      Save
                    </Button>
                    <Button
                      onClick={() => { setEditingName(false); setNameValue(u.name || ""); }}
                      variant="outline"
                      size="sm"
                      className="h-8"
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-zinc-200">{u.name || <em className="text-zinc-500">no name set</em>}</span>
                    <button
                      onClick={() => setEditingName(true)}
                      data-testid="edit-name-btn"
                      className="text-zinc-500 hover:text-orange-400"
                    >
                      <PencilSimple size={12} />
                    </button>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <CalendarBlank size={14} className="text-zinc-500" />
                <span className="text-zinc-400 text-[13px]">Joined {shortDate(u.created_at)} · {relTime(u.created_at)}</span>
              </div>
              <div className="flex items-center gap-2">
                {u.role === "admin" ? <Crown size={14} weight="fill" className="text-orange-400" /> : <UserIcon size={14} className="text-zinc-500" />}
                <span className={u.role === "admin" ? "text-orange-300 font-medium" : "text-zinc-300"}>{u.role}</span>
                {!isMe && (
                  <button
                    onClick={toggleRole}
                    disabled={busy}
                    data-testid="toggle-role-btn"
                    className="text-[10px] font-mono uppercase tracking-wider text-orange-400 hover:text-orange-300 ml-auto disabled:opacity-50"
                  >
                    {u.role === "admin" ? "→ demote" : "→ promote to admin"}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Subscription */}
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-sm p-4">
            <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-2">Subscription</div>
            {sub ? (
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Tier</div>
                  <div className="mt-1"><TierPill tier={sub.tier} status={sub.status} /></div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Renews</div>
                  <div className="mt-1 text-zinc-300 font-mono text-[12px]">{shortDate(sub.current_period_end)}</div>
                </div>
                {sub.trial_ends_at && (
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Trial ends</div>
                    <div className="mt-1 text-amber-300 font-mono text-[12px]">{shortDate(sub.trial_ends_at)}</div>
                  </div>
                )}
                {sub.cancel_at_period_end && (
                  <div className="col-span-2 text-[11px] text-red-400 font-mono">Will cancel at period end</div>
                )}
              </div>
            ) : (
              <div className="text-sm text-zinc-500 italic">No subscription record — user is on default Free tier.</div>
            )}
          </div>

          {/* Activity */}
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-sm p-4">
            <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-3">Activity</div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <Buildings size={16} className="text-orange-400 mx-auto mb-1" weight="duotone" />
                <div className="text-lg font-display font-semibold text-zinc-100">{detail.lead_count}</div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">leads</div>
              </div>
              <div>
                <Handshake size={16} className="text-emerald-400 mx-auto mb-1" weight="duotone" />
                <div className="text-lg font-display font-semibold text-zinc-100">{detail.referrals_as_referrer}</div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">referred</div>
              </div>
              <div>
                <CreditCard size={16} className="text-purple-400 mx-auto mb-1" weight="duotone" />
                <div className="text-lg font-display font-semibold text-zinc-100">{detail.payments?.length || 0}</div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">payments</div>
              </div>
            </div>

            {detail.recent_leads?.length > 0 && (
              <div className="mt-4 pt-3 border-t border-zinc-800/60">
                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-2">Recent leads</div>
                <div className="space-y-1.5">
                  {detail.recent_leads.map((l) => (
                    <div key={l.id} className="flex items-center justify-between text-[12px]">
                      <div className="min-w-0 flex-1">
                        <div className="text-zinc-200 truncate">{l.business_name}</div>
                        <div className="text-zinc-500 font-mono text-[10px]">{l.city}{l.state ? `, ${l.state}` : ""}</div>
                      </div>
                      <div className="text-[10px] font-mono text-zinc-500">{l.status}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Danger zone */}
          {!isMe && (
            <div className="border border-red-500/30 bg-red-500/[0.04] rounded-sm p-4">
              <div className="text-[10px] font-mono uppercase tracking-wider text-red-400 mb-2">Danger zone</div>
              {!confirmDelete ? (
                <Button
                  onClick={() => setConfirmDelete(true)}
                  variant="outline"
                  data-testid="delete-user-btn"
                  className="w-full border-red-500/40 text-red-300 hover:bg-red-500/10 hover:text-red-200"
                >
                  <TrashSimple size={14} weight="fill" className="mr-2" /> Delete this user
                </Button>
              ) : (
                <div className="space-y-2">
                  <div className="text-[12px] text-red-200">
                    This permanently deletes the user, their <strong>{detail.lead_count} leads</strong>, subscriptions, and saved searches. Cannot be undone.
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={doDelete}
                      disabled={busy}
                      data-testid="confirm-delete-btn"
                      className="flex-1 bg-red-600 hover:bg-red-500 text-white"
                    >
                      {busy ? <Spinner size={14} className="animate-spin" /> : "Yes, delete permanently"}
                    </Button>
                    <Button
                      onClick={() => setConfirmDelete(false)}
                      variant="outline"
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---- Main page ---------------------------------------------------------

export default function AdminUsers() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (!isAdmin) return;
    let live = true;
    const params = q.trim() ? { q: q.trim() } : {};
    api.get("/admin/users", { params })
      .then((r) => { if (live) setRows(r.data.users || []); })
      .catch(() => { if (live) setRows([]); });
    return () => { live = false; };
  }, [q, reloadTick, isAdmin]);

  const refresh = () => setReloadTick((t) => t + 1);

  const stats = useMemo(() => {
    if (!rows) return null;
    return {
      total: rows.length,
      admins: rows.filter((r) => r.role === "admin").length,
      pro: rows.filter((r) => r.tier === "pro" && r.status !== "inactive").length,
      trialing: rows.filter((r) => r.status === "trialing").length,
    };
  }, [rows]);

  if (!isAdmin) return <Navigate to="/profile" replace />;

  return (
    <AppShell>
      <PageHeader
        title="Users"
        subtitle="Every account on ClickForge. Click any row for the full detail drawer."
        icon={UsersThree}
      />

      <div className="space-y-4">
        {/* Stats strip */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-zinc-900/40 border border-zinc-800 rounded-sm p-3">
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Total</div>
              <div className="text-2xl font-display font-semibold text-zinc-100 mt-1" data-testid="admin-users-total">{stats.total}</div>
            </div>
            <div className="bg-zinc-900/40 border border-zinc-800 rounded-sm p-3">
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Pro Active</div>
              <div className="text-2xl font-display font-semibold text-orange-300 mt-1">{stats.pro}</div>
            </div>
            <div className="bg-zinc-900/40 border border-zinc-800 rounded-sm p-3">
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Trialing</div>
              <div className="text-2xl font-display font-semibold text-amber-300 mt-1">{stats.trialing}</div>
            </div>
            <div className="bg-zinc-900/40 border border-zinc-800 rounded-sm p-3">
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Admins</div>
              <div className="text-2xl font-display font-semibold text-zinc-100 mt-1">{stats.admins}</div>
            </div>
          </div>
        )}

        {/* Search + refresh */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by email or name…"
              data-testid="admin-users-search"
              className="pl-9 bg-zinc-900/40 border-zinc-800 focus:border-orange-500"
            />
          </div>
          <Button
            onClick={refresh}
            variant="outline"
            data-testid="admin-users-refresh"
            className="border-zinc-800 hover:border-orange-500/40 hover:text-orange-400"
          >
            <ArrowClockwise size={14} />
          </Button>
        </div>

        {/* Table */}
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-sm overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2 text-[10px] font-mono uppercase tracking-wider text-zinc-500 border-b border-zinc-800 bg-zinc-950/40">
            <div className="col-span-4">User</div>
            <div className="col-span-2">Role</div>
            <div className="col-span-2">Tier</div>
            <div className="col-span-2 text-right">Leads · Refs</div>
            <div className="col-span-2 text-right">Joined</div>
          </div>

          {rows === null ? (
            <div className="p-12 text-center text-zinc-500">
              <Spinner size={20} className="animate-spin mx-auto" />
            </div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center text-zinc-500 text-sm" data-testid="admin-users-empty">
              {q ? `No users match "${q}"` : "No users yet."}
            </div>
          ) : (
            <div className="divide-y divide-zinc-800/60">
              {rows.map((u) => (
                <button
                  key={u.id}
                  onClick={() => setSelectedId(u.id)}
                  data-testid={`admin-user-row-${u.id}`}
                  className="w-full text-left grid grid-cols-12 gap-3 px-4 py-3 hover:bg-zinc-800/30 transition-colors items-center"
                >
                  <div className="col-span-12 md:col-span-4 min-w-0">
                    <div className="text-sm text-zinc-100 truncate">{u.name || <em className="text-zinc-500">no name</em>}</div>
                    <div className="text-[11px] font-mono text-zinc-500 truncate">{u.email}</div>
                  </div>
                  <div className="col-span-4 md:col-span-2 flex items-center gap-1.5">
                    {u.role === "admin" ? (
                      <><Crown size={12} weight="fill" className="text-orange-400" /> <span className="text-[12px] text-orange-300">admin</span></>
                    ) : (
                      <span className="text-[12px] text-zinc-400">user</span>
                    )}
                  </div>
                  <div className="col-span-4 md:col-span-2">
                    <TierPill tier={u.tier} status={u.status} />
                  </div>
                  <div className="col-span-4 md:col-span-2 md:text-right font-mono text-[11px] text-zinc-400">
                    {u.lead_count} · {u.referrals_credited}
                  </div>
                  <div className="hidden md:block col-span-2 text-right font-mono text-[11px] text-zinc-500">
                    {relTime(u.created_at)}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {selectedId && (
        <UserDetailDrawer
          userId={selectedId}
          isMe={selectedId === user?.id}
          onClose={() => setSelectedId(null)}
          onUpdated={refresh}
          onDeleted={refresh}
        />
      )}
    </AppShell>
  );
}
